import React, { useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Loading } from '@app/react-common';
import { getSimpleListings } from 'api/get';
import { formatCurrency } from 'utils';
import {
  Checkbox,
  Empty,
  Icon,
  ListingImage,
  Modal,
  ModalProps,
  Text,
} from 'components';
import { syncListingShowings } from 'api/AlmanacController';
import type { SimpleListing } from '@/app-package';
import { useShowingsCalendarContext } from './ShowingsCalendarContext';
import AgentUserCalendarContext from './AgentUserCalendarContext';

const SyncByListingModal: React.FC<ModalProps> = () => {
  const {
    distinctListings,
    setCalendarModalVisible,
    syncByListingModalVisible,
    setSyncByListingModalVisible,
    listingsToSync,
    setListingsToSync,
  } = useShowingsCalendarContext();
  const agentUserCalendarContext = React.useContext(AgentUserCalendarContext);

  const [loading, setLoading] = React.useState(false);
  const [listings, setListings] = React.useState<
    (SimpleListing & {
      province: string;
      city: string;
      postal_code: string;
      thumbnail: string;
    })[]
  >([]);

  useEffect(() => {
    const fetchSimpleListings = async () => {
      if (distinctListings) {
        setLoading(true);
        const simpleListings = await getSimpleListings(distinctListings);
        // @ts-ignore
        setListings(simpleListings);
        setLoading(false);
      }
    };
    fetchSimpleListings();
  }, [distinctListings]);

  useEffect(() => {
    if (syncByListingModalVisible) setListingsToSync([]);
  }, [syncByListingModalVisible]);

  return (
    <Modal
      title="Sync by listing"
      open={syncByListingModalVisible}
      onOk={() => {
        setSyncByListingModalVisible(false);
        if (agentUserCalendarContext.isAuthenticated) {
          syncListingShowings({ listingIds: listingsToSync });
        } else {
          setCalendarModalVisible(true);
        }
      }}
      okButtonProps={{ disabled: listingsToSync.length === 0 }}
      onCancel={() => setSyncByListingModalVisible(false)}
      okText="Sync"
      bodyStyle={{ padding: '16px' }}
    >
      <Text color="rgba(0,0,0,0.65)">
        Select which listings’ showings you want to sync to your calendar
      </Text>
      {loading ? (
        <Loading />
      ) : listings.length ? (
        <Container>
          {listings.map(listing => (
            <AlignedRow key={listing.id}>
              <StyledCheckbox
                checked={listingsToSync.includes(listing.id)}
                onClick={e => {
                  e.stopPropagation();
                  setListingsToSync(
                    listingsToSync.includes(listing.id)
                      ? listingsToSync.filter(id => id !== listing.id)
                      : [...listingsToSync, listing.id],
                  );
                }}
              />
              <ImageContainer>
                <ListingImage size={36} imageSrc={listing.thumbnail} />
              </ImageContainer>
              <Column>
                <AlignedRow>
                  <StyledAddress>{listing.line1}</StyledAddress>
                  <StyledInfoText>
                    {listing.city}, {listing.province}
                  </StyledInfoText>
                </AlignedRow>
                <AlignedInfoRow>
                  <StyledInfoText>
                    {formatCurrency(listing.list_price)}
                  </StyledInfoText>
                  {listing.listedAt && (
                    <>
                      <StyledIcon
                        type="antdClockCircle"
                        height={10.5}
                        width={10.5}
                      />
                      <StyledInfoText>
                        {moment(moment()).diff(listing.listedAt, 'days')}d
                      </StyledInfoText>
                    </>
                  )}
                </AlignedInfoRow>
              </Column>
            </AlignedRow>
          ))}
        </Container>
      ) : (
        <StyledEmpty />
      )}
    </Modal>
  );
};

const StyledIcon = styled(Icon)`
  align-self: top;
  margin-right: 4px;
  & .anticon-clock-circle {
    line-height: 22px;
  }
`;

const StyledAddress = styled.div`
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin-right: 8px;
`;

const StyledInfoText = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  justify-self: center;
  align-self: end;
  margin-right: 8px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 4px 8px;
`;

const ImageContainer = styled.div`
  padding: 4px 8px;
`;

const AlignedRow = styled.div`
  display: flex;
  align-items: center;
`;

const AlignedInfoRow = styled(AlignedRow)`
  height: 20px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 18px;
  gap: 18px;
`;

const StyledEmpty = styled(Empty)`
  margin-top: 18px;
`;

export default SyncByListingModal;
